import { FC, useEffect, useState } from 'react';
import { Listbox } from '@headlessui/react';
import { BiChevronDown } from 'react-icons/bi';
import { useTranslation } from 'react-i18next';

/**
 * @description Properties interface for the FormPhoneInputSelect component
 */
interface Props {
  id: string;
  countryCodes: string[];
  onCodeChange: (countryCode: string) => void;
  countryCode?: string;
}

/**
 * React Functional Component for rendering a phone input select box
 * @param props - Props for the component
 * @returns JSX for the component
 */
const FormPhoneInputSelect: FC<Props> = ({ countryCodes, onCodeChange, countryCode = 'BR' }) => {
  const [selectedCountry, setSelectedCountry] = useState(countryCode);
  const { t } = useTranslation();

  useEffect(() => {
    setSelectedCountry(countryCode || 'BR');
  }, [countryCode]);

  /**
   * Handles changes in the selected country
   * @param value - The value of the selected country
   */
  const handleChange = (value: string) => {
    setSelectedCountry(value);
    onCodeChange(value);
  };

  return (
    <Listbox as="div" className="relative" onChange={handleChange}>
      {/* button  */}
      <Listbox.Button
        as="button"
        className="flex h-[40px] items-center gap-2 rounded-lg border-[1px] border-[#CED4DA] bg-white px-3 pr-2 text-sm text-slate-800 shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 hover:border-blue-400"
      >
        {selectedCountry && (
          <img
            alt=""
            className="aspect-square w-[20px] flex-shrink-0"
            src={`http://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedCountry}.svg`}
          />
        )}
        <span className="max-w-[120px] truncate">{t(`countryCodes.${selectedCountry}`)}</span>
        <BiChevronDown className="text-lg text-slate-500 flex-shrink-0" />
      </Listbox.Button>

      {/* dropdown  */}
      <Listbox.Options
        as="ul"
        className="absolute z-10 h-[50vh] translate-y-[-50%] transform overflow-y-auto border border-gray-500 bg-white py-1 shadow-lg"
      >
        {/* option list */}
        {countryCodes.map((country) => (
          <Listbox.Option key={country} value={country}>
            {({ active }) => (
              <li
                className={`${
                  active ? 'bg-primary text-white' : 'text-gray-900'
                } relative flex cursor-pointer select-none py-2 pl-4 pr-8`}
              >
                <img
                  alt=""
                  className="mr-2 aspect-square w-[20px]"
                  src={`http://purecatamphetamine.github.io/country-flag-icons/3x2/${country}.svg`}
                />
                {t(`countryCodes.${country}`)}
              </li>
            )}
          </Listbox.Option>
        ))}
      </Listbox.Options>
    </Listbox>
  );
};

export { FormPhoneInputSelect };
